import { useState, useEffect } from 'react';
import { buildNearbyGroups, NEARBY_RADIUS_MI } from '../services/nearby/buildNearbyGroups';
import { geocodeCity, reverseGeocodeCity } from '../services/location/locationService';
import {
  getConnectionLocationRecords,
  getMyLiveLocation,
} from '../services/location/liveLocationService';
import { getProfileData } from '../services/storage/userDataStorage';
import { readCache, writeCache } from '../services/storage/readCache';
import { getSharedHometown } from '../utils/connectionName';
import { isFullTier } from '../utils/userTier';

const MY_LIVE_CACHE_KEY = 'my_live_location_cache';
const RECORDS_CACHE_KEY = 'connection_location_records_cache';

/**
 * useNearbyGroups
 *
 * Resolves each profile connection's area — live neighborhood when both users
 * share (reciprocal), else the consent-gated hometown they shared with you,
 * geocoded on read — plus the viewer's anchor, then groups them via the pure
 * buildNearbyGroups algorithm.
 *
 * Profile (Royale) peers only; anon/manual contacts are excluded (no shared
 * hometown, no live location). senderProfileUserId is the app's established
 * profile-peer signal (same one the deletion-detection latch uses).
 *
 * Re-resolves when connections or the sharing flag change.
 *
 * @param {Array} connections - enriched connection objects from the list page
 * @param {Object} [opts]
 * @param {boolean} [opts.isLocationSharing] - viewer's sharing flag (reciprocity gate)
 * @param {boolean} [opts.enabled] - only resolve when the Nearby tab is active
 * @param {number} [opts.radiusMi]
 * @param {Object} [opts.testLiveRecords] - DEV: synthetic live records (chatRoomId → {lat,lng}) merged in, bypassing the sharing gate
 * @returns {{ groups: Array, hasAnchor: boolean, loading: boolean }}
 */
export const useNearbyGroups = (
  connections,
  {
    isLocationSharing = false,
    enabled = true,
    radiusMi = NEARBY_RADIUS_MI,
    testLiveRecords = null,
  } = {}
) => {
  const [state, setState] = useState({ groups: [], hasAnchor: false, loading: true });

  useEffect(() => {
    // Skip the geocoding work entirely until the Nearby tab is opened
    if (!enabled) {
      setState({ groups: [], hasAnchor: false, loading: false });
      return undefined;
    }

    let cancelled = false;

    // Resolve groups from a given set of inputs — called once with cached
    // inputs (instant paint; geocode caches are already warm) and again with
    // fresh DB inputs (silent correction).
    const resolveWith = async ({ liveRecords, myLive, profileData }) => {
      // Profile peers only (anon/manual excluded)
      const peers = (connections || []).filter(
        (c) => !c.isManualContact && c.anonData?.senderProfileUserId
      );

      // Live records (real, reciprocity-gated) + any dev test records (bypass gate)
      const allLive = { ...liveRecords, ...(testLiveRecords || {}) };

      // Viewer anchor: own live coords if sharing, else hometown geocoded.
      // City/country come from REVERSE-geocoding the coords (the stored city is
      // an abbreviation that forward-geocodes wrong — "LA"→Laredo).
      let anchor = null;
      if (isLocationSharing && myLive?.lat != null) {
        const g = await reverseGeocodeCity(myLive.lat, myLive.lng);
        anchor = { lat: myLive.lat, lng: myLive.lng, countryCode: g?.countryCode ?? null };
      } else if (profileData?.currentCity) {
        const g = await geocodeCity(profileData.currentCity);
        if (g?.lat != null) anchor = { lat: g.lat, lng: g.lng, countryCode: g.countryCode };
      }

      // Resolve each peer's coords/city/country
      const resolved = [];
      for (const c of peers) {
        const live = allLive[c.chatRoomId];
        const isTestLive = !!(testLiveRecords && testLiveRecords[c.chatRoomId]);
        const sharedHometown = getSharedHometown(c.anonData);
        let lat = null;
        let lng = null;
        let city = null;
        let countryCode = null;

        if ((isLocationSharing || isTestLive) && live?.lat != null) {
          // Both sharing → live location. Canonicalize city/country by REVERSE-
          // geocoding the authoritative coords, not the abbreviated stored city.
          lat = live.lat;
          lng = live.lng;
          const g = await reverseGeocodeCity(live.lat, live.lng);
          city = g?.city ?? live.city ?? null;
          countryCode = g?.countryCode ?? null;
        } else if (sharedHometown) {
          // Else the hometown they shared with you (consent-gated)
          const g = await geocodeCity(sharedHometown);
          if (g?.lat != null) {
            lat = g.lat;
            lng = g.lng;
            city = g.city;
            countryCode = g.countryCode;
          }
        }

        if (lat != null && lng != null) {
          resolved.push({
            chatRoomId: c.chatRoomId,
            lat,
            lng,
            city,
            countryCode,
            metAt: c.connectionMetadata?.meetingDate || null,
            connection: c,
          });
        }
      }

      const groups = buildNearbyGroups(resolved, anchor, { radiusMi });
      return { groups, hasAnchor: !!anchor };
    };

    const run = async () => {
      const profileUserId = localStorage.getItem('profile_user_id');
      // Live-location reads are Full-only (Nearby is a Full feature); the
      // local hometown-anchor grouping below stays available to all tiers.
      const wantLive = Boolean(isLocationSharing && profileUserId && isFullTier());
      const profileData = await getProfileData();

      if (!wantLive) {
        // Hometown-anchor path: all inputs are local (profile data + geocode
        // cache) — resolve once and finish. No DB round trips.
        const result = await resolveWith({ liveRecords: {}, myLive: null, profileData });
        if (!cancelled) setState({ ...result, loading: false });
        return;
      }

      // 1) Cache paint: last-known live inputs, if any (stale-while-revalidate)
      const cachedRecords = readCache(RECORDS_CACHE_KEY);
      const cachedMyLive = readCache(MY_LIVE_CACHE_KEY);
      if (cachedRecords.hit || cachedMyLive.hit) {
        const painted = await resolveWith({
          liveRecords: cachedRecords.hit ? cachedRecords.value : {},
          myLive: cachedMyLive.hit ? cachedMyLive.value : null,
          profileData,
        });
        if (!cancelled) setState({ ...painted, loading: false });
      }

      // 2) Fresh from the DB (fetchers throw on failure — see run().catch),
      //    overwrite caches, re-resolve.
      const [liveRecords, myLive] = await Promise.all([
        getConnectionLocationRecords(profileUserId),
        getMyLiveLocation(profileUserId),
      ]);
      writeCache(RECORDS_CACHE_KEY, liveRecords);
      writeCache(MY_LIVE_CACHE_KEY, myLive);
      const fresh = await resolveWith({ liveRecords, myLive, profileData });
      if (!cancelled) setState({ ...fresh, loading: false });
    };

    setState((s) => ({ ...s, loading: true }));
    run().catch((err) => {
      console.error('[NEARBY] resolve failed:', err);
      // Keep whatever is on screen (previous or cache-painted groups) — a
      // failed refresh must never downgrade to the empty/enable state.
      if (!cancelled) setState((s) => ({ ...s, loading: false }));
    });

    return () => {
      cancelled = true;
    };
  }, [connections, isLocationSharing, enabled, radiusMi, testLiveRecords]);

  return state;
};
